import HeraCassetteLabelsDialog from '../themes/Hera/components/CassetteLabels/HeraCassetteLabelsDialog.vue';
import { asyncIcon } from './asyncIcons';
export { useGeolocationTenantBehavior } from '../themes/Pompei/composables/useGeolocationTenantBehavior';
export { resolveDomainSpecificTabComponent } from '../themes/Pompei/utils/domainSpecificTabs';
export { resolveDatabaseHeaderFeature } from '../themes/Pompei/utils/databaseHeaderFeature';
export { resolveCatalogMapFeature } from '../themes/Omnia/utils/catalogMapFeature';
export { projectBibliographyAuthor } from '../modules/simbolo/bibliographyAuthorProjection';
export {
    vueformExtensionElements,
    sheetContentWidgetFactories,
    schemaFieldExtensionHandlers,
    newSchedaExtensionFactories,
} from './compatibilityExtensions';
import {
    buildHeraCassetteLabelPayload,
    canPrintHeraCassetteLabel,
    countHeraCassetteLabelSelection,
    useHeraCassetteLabelPrinting,
} from '../themes/Hera/composables/useHeraCassetteLabelPrinting';

const pageModules = import.meta.glob('../themes/*/Pages/**/*.vue');
const layoutModules = import.meta.glob('../themes/*/Layouts/AuthenticatedLayout.vue');
const themeComponentModules = import.meta.glob('../themes/*/Components/**/*.vue');
const coreMessageModules = import.meta.glob('../i18n/locales/*/core.json');
const themeMessageModules = import.meta.glob('../themes/*/i18n/*.json');
const themeVueModules = import.meta.glob('../../assets/themes/*/icons/*.vue');
const sharedVueModules = import.meta.glob('../../assets/icons/*.vue');
const themeSvgUrls = import.meta.glob('../../assets/themes/*/icons/*.svg', {
    eager: true,
    query: '?url',
    import: 'default',
});
const sharedSvgUrls = import.meta.glob('../../assets/icons/*.svg', {
    eager: true,
    query: '?url',
    import: 'default',
});

const buildRegistry = (modules, pattern) => {
    const registry = {};

    for (const [path, module] of Object.entries(modules)) {
        const match = path.match(pattern);
        if (match) {
            registry[`${match[1]}/${match[2]}`] = module;
        }
    }

    return registry;
};

const pages = buildRegistry(pageModules, /\/themes\/([^/]+)\/Pages\/(.+)\.vue$/);
const layouts = buildRegistry(
    layoutModules,
    /\/themes\/([^/]+)\/Layouts\/(AuthenticatedLayout)\.vue$/,
);
const themeComponentsModules = buildRegistry(
    themeComponentModules,
    /\/themes\/([^/]+)\/Components\/(.+)\.vue$/,
);
const buildThemeIconRegistry = (modules, extension) => {
    const registry = {};
    const pattern = new RegExp(`/themes/([^/]+)/icons/(.+)\\.${extension}$`);

    for (const [path, module] of Object.entries(modules)) {
        const match = path.match(pattern);
        if (match) {
            registry[`${match[1]}/${match[2]}`] = extension === 'vue' ? asyncIcon(module) : module;
        }
    }

    return registry;
};
const buildSharedIconRegistry = (modules, extension) => {
    const registry = {};
    const pattern = new RegExp(`/icons/(.+)\\.${extension}$`);

    for (const [path, module] of Object.entries(modules)) {
        const match = path.match(pattern);
        if (match) {
            registry[match[1]] = extension === 'vue' ? asyncIcon(module) : module;
        }
    }

    return registry;
};

const themeComponents = buildThemeIconRegistry(themeVueModules, 'vue');
const sharedComponents = buildSharedIconRegistry(sharedVueModules, 'vue');
const themeUrls = buildThemeIconRegistry(themeSvgUrls, 'svg');
const sharedUrls = buildSharedIconRegistry(sharedSvgUrls, 'svg');

export const registryTarget = null;
export const resolvePublicStaticCache = (theme = 'Default') => theme === 'Omnia'
    ? 'omnia:public-static-v1' : 'pompei:public-static-v4';
export const registryThemes = Object.freeze([
    ...new Set(Object.keys(layouts).map((key) => key.split('/')[0])),
].sort());

const heraCassetteLabelPrintingFeature = Object.freeze({
    component: HeraCassetteLabelsDialog,
    buildPayload: buildHeraCassetteLabelPayload,
    canPrint: canPrintHeraCassetteLabel,
    countSelection: countHeraCassetteLabelSelection,
    usePrinting: useHeraCassetteLabelPrinting,
});

export const resolveCassetteLabelPrintingFeature = (theme = 'Default') => (
    theme === 'Hera' ? heraCassetteLabelPrintingFeature : null
);

export const resolvePageImporter = (name, theme = 'Default') => {
    return pages[`${theme}/${name}`] ?? pages[`Default/${name}`] ?? null;
};

export const resolveLayoutImporter = (theme = 'Default') => {
    return layouts[`${theme}/AuthenticatedLayout`]
        ?? layouts['Default/AuthenticatedLayout']
        ?? null;
};

export const resolveIcon = (name, { theme = 'Default', shared = false } = {}) => {
    if (shared) {
        if (sharedComponents[name]) return { kind: 'component', value: sharedComponents[name] };
        if (sharedUrls[name]) return { kind: 'svg', value: sharedUrls[name] };
        return null;
    }

    for (const candidate of [...new Set([theme, 'Default'])]) {
        const key = `${candidate}/${name}`;
        if (themeComponents[key]) return { kind: 'component', value: themeComponents[key] };
        if (themeUrls[key]) return { kind: 'svg', value: themeUrls[key] };
    }

    if (sharedComponents[name]) return { kind: 'component', value: sharedComponents[name] };
    if (sharedUrls[name]) return { kind: 'svg', value: sharedUrls[name] };

    return null;
};

export const resolveThemedIconUrl = (name, theme = 'Default') => {
    return themeUrls[`${theme}/${name}`] ?? themeUrls[`Default/${name}`] ?? null;
};

export const resolveThemeComponentImporter = (name, theme = 'Default') => {
    return themeComponentsModules[`${theme}/${name}`] ?? null;
};

const localeLanguage = (locale) => String(locale || 'it').replaceAll('_', '-').split('-')[0].toLowerCase();
const loadMessages = async (importer) => importer
    ? (await importer()).default ?? {}
    : {};

export const resolveFrontendMessages = async (theme = 'Default', locales = ['it']) => {
    const requestedLocales = [...new Set(['it', ...locales.map(localeLanguage)])];
    const messages = {};

    await Promise.all(requestedLocales.map(async (locale) => {
        const [core, defaults, themed] = await Promise.all([
            loadMessages(coreMessageModules[`../i18n/locales/${locale}/core.json`]),
            loadMessages(themeMessageModules[`../themes/Default/i18n/${locale}.json`]),
            theme === 'Default'
                ? Promise.resolve({})
                : loadMessages(themeMessageModules[`../themes/${theme}/i18n/${locale}.json`]),
        ]);

        messages[locale] = { ...core, ...defaults, ...themed };
    }));

    return messages;
};

export const resolveThemeDefaultImageUrl = (theme = 'Default') => {
    const selectedTheme = registryThemes.includes(theme) ? theme : 'Default';

    return `/assets/themes/${selectedTheme}/default.webp`;
};

export const resolveServiceWorkerUrl = (tenantSlug = 'default') => {
    return `/service-worker.js?tenant=${tenantSlug || 'default'}`;
};
